/**
 * Mock-Daten-Generator fuer Scene 1 — Channel-Load + Stress-Heatmap.
 *
 * Komplett deterministisch (kein Math.random) damit der Demo-Call
 * reproduzierbar ist. Erzeugt pseudo-realistische Warehouse-Aktivitaet
 * ueber einen 24h-Arbeitstag mit Schicht-Wechseln und Spikes.
 *
 * Generische Zonen aus demo-constants.ts — Site Alpha, keine Site-spezifika.
 */
import { DEMO_ZONES } from "../demo-constants";

export interface ZoneActivity {
  /** Minute des Tages (0-1439). */
  minute: number;
  /** Zonen-ID aus DEMO_ZONES. */
  zoneId: string;
  /** PTT-Events in den letzten 5 Minuten (0-50). */
  pttRate: number;
  /** Akustischer Stress-Score (0-100, k>=5 anonymisiert). */
  stressScore: number;
}

/** Pseudo-deterministische Sinus-basierte Aktivitaets-Kurve pro Zone. */
function activityCurve(minute: number, zoneIdx: number): number {
  // Schicht-Pattern: 06:00 ramp up, 14:00 peak, 22:00 quiet
  const hour = minute / 60;
  const shiftBase = Math.max(0, Math.sin((hour - 6) * Math.PI / 16)) * 0.8 + 0.1;
  // Zonen-Phasen-Shift (jede Zone hat andere Peak-Zeit)
  const zoneShift = Math.cos((hour - 8 - zoneIdx * 1.5) * Math.PI / 12) * 0.3 + 0.5;
  // Combine
  return Math.max(0, Math.min(1, shiftBase * zoneShift));
}

/**
 * Erzeugt fuer alle 6 Zonen + alle 24h * 60 Min eine Activity-Sample.
 * 5-Minuten-Buckets — 288 Samples pro Zone.
 */
export function generateDayActivity(): ZoneActivity[] {
  const samples: ZoneActivity[] = [];
  for (let m = 0; m < 1440; m += 5) {
    for (let zi = 0; zi < DEMO_ZONES.length; zi++) {
      const intensity = activityCurve(m, zi);
      // PTT-Rate scales 0-50
      const pttRate = Math.round(intensity * 50);
      // Stress-Score scales 30-85 (never zero — there's always baseline operations stress)
      const stressScore = Math.round(30 + intensity * 55);
      samples.push({
        minute: m,
        zoneId: DEMO_ZONES[zi].id,
        pttRate,
        stressScore,
      });
    }
  }
  return samples;
}

/** Aktivitaets-Werte fuer einen konkreten Zeitpunkt (in Minuten). */
export function activityAtMinute(
  samples: ZoneActivity[],
  minute: number,
): Map<string, ZoneActivity> {
  const bucket = Math.floor(minute / 5) * 5;
  const result = new Map<string, ZoneActivity>();
  for (const s of samples) {
    if (s.minute === bucket) result.set(s.zoneId, s);
  }
  return result;
}

/** Formatiert Minute des Tages als "HH:MM". */
export function formatTimeOfDay(minute: number): string {
  const h = Math.floor(minute / 60);
  const m = minute % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Findet die "Insight" — die hoechste Spike im aktuellen Zeitpunkt,
 * vergleicht gegen 7-Tage-Baseline. AMZEL-Narrator-Output.
 */
export function insightForMinute(
  samples: ZoneActivity[],
  minute: number,
): { zoneLabel: string; pttRate: number; pttMultiplier: number; stressScore: number } | null {
  const current = activityAtMinute(samples, minute);
  let topZone: ZoneActivity | null = null;
  for (const a of current.values()) {
    if (!topZone || a.pttRate > topZone.pttRate) topZone = a;
  }
  if (!topZone || topZone.pttRate < 15) return null;
  const zoneLabel = DEMO_ZONES.find((z) => z.id === topZone!.zoneId)?.label ?? topZone.zoneId;
  // Pseudo-baseline = stable mid-day baseline of ~20
  const baseline = 20;
  return {
    zoneLabel,
    pttRate: topZone.pttRate,
    pttMultiplier: Number((topZone.pttRate / baseline).toFixed(1)),
    stressScore: topZone.stressScore,
  };
}
